import type {
  RohsComponentComplianceFileView,
  RohsProductComplianceFileView,
} from '@/shared/hooks/api/rohsComplianceFiles'
import { trackedRohsParts } from '@/v2/lib/trackedRohsParts'

export const PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_REQUIREMENT_KEYS =
  [
    'manufacturerIdentity',
    'declarationObject',
    'componentEvidence',
    'productExportable',
  ] as const

export type RohsProductComplianceFileDeclarationOfConformityRequirementKey =
  (typeof PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_REQUIREMENT_KEYS)[number]

export type RohsProductComplianceFileDeclarationOfConformityRequirement = {
  key: RohsProductComplianceFileDeclarationOfConformityRequirementKey
  met: boolean
}

type EvaluateInput = {
  productName?: string | null
  productSku?: string | null
  productDescription?: string | null
  rohsApplies: boolean
  productFile: RohsProductComplianceFileView | null
  components: RohsComponentComplianceFileView[]
  manufacturerName?: string | null
  manufacturerAddress?: string | null
}

function hasComponentEvidence(components: RohsComponentComplianceFileView[]): boolean {
  const tracked = trackedRohsParts(components)
  if (tracked.length === 0) {
    return false
  }
  return tracked.every((part) => part.completedAt != null)
}

function isProductExportable(input: EvaluateInput): boolean {
  if (!input.rohsApplies) {
    return false
  }
  if (input.productFile?.status !== 'ACTIVE') {
    return false
  }
  return true
}

export function evaluateRohsProductComplianceFileDeclarationOfConformityRequirements(
  input: EvaluateInput
): {
  requirements: RohsProductComplianceFileDeclarationOfConformityRequirement[]
  allMet: boolean
  generateReady: boolean
  unmetKeys: RohsProductComplianceFileDeclarationOfConformityRequirementKey[]
  unmetKeysExceptManufacturer: RohsProductComplianceFileDeclarationOfConformityRequirementKey[]
} {
  const manufacturerMet =
    Boolean(input.manufacturerName?.trim()) &&
    Boolean(input.manufacturerAddress?.trim())
  const declarationObjectMet =
    Boolean(input.productName?.trim()) &&
    Boolean(input.productSku?.trim()) &&
    Boolean(input.productDescription?.trim())
  const componentEvidenceMet = hasComponentEvidence(input.components)
  const productExportableMet = isProductExportable(input)

  const requirements: RohsProductComplianceFileDeclarationOfConformityRequirement[] =
    [
      { key: 'manufacturerIdentity', met: manufacturerMet },
      { key: 'declarationObject', met: declarationObjectMet },
      { key: 'componentEvidence', met: componentEvidenceMet },
      { key: 'productExportable', met: productExportableMet },
    ]

  const unmetKeys = requirements
    .filter((requirement) => !requirement.met)
    .map((requirement) => requirement.key)
  const unmetKeysExceptManufacturer = unmetKeys.filter(
    (key) => key !== 'manufacturerIdentity'
  )

  return {
    requirements,
    allMet: unmetKeys.length === 0,
    generateReady: unmetKeysExceptManufacturer.length === 0,
    unmetKeys,
    unmetKeysExceptManufacturer,
  }
}
